import React from 'react'
import {
  Button,
  Input,
  InputRef,
  Modal,
  Radio,
  Space,
  Table,
  Tag,
  Typography
} from 'antd'
import { FilterFilled, PlusOutlined, SearchOutlined } from '@ant-design/icons'
import debounce from 'lodash.debounce'
import { ColumnsType } from 'antd/es/table'
import { MenuInfo } from 'rc-menu/lib/interface'
import { StatusDropdown } from 'components/ui/StatusDropdown/StatusDropdown'
import { productApi } from 'api/product'
import noProductImage from 'assets/images/no-image-product.png'
import {
  EProductStatus,
  IChangeVariationProduct,
  IFrontVariation,
  IFrontVariationProduct,
  IProductQueryFilters,
  IProductResponse
} from 'models/product'
import { ERoles } from 'models/auth'
import { DEBOUNCE_DELAY } from 'shared/constants/app'
import { IMAGES_URL } from 'shared/constants/urls'
import { getNormalizedPrice } from 'shared/utils/getNormalizedPrice'
import { useAppSelector } from 'shared/hooks/useReduxHooks'
import {
  getProductStatusAction,
  getProductStatusItems,
  getStatusAfterAction,
  productTags
} from 'shared/constants/product'
import { showServerError } from 'shared/utils/showServerError'
import { appSelectors } from 'store/app'
import { showMessage } from 'shared/utils/showMessage'
import styles from '../ProductForm.module.scss'
import { SelectedVariation } from './SelectedVariation'

interface IChooseVariationProductModalProps {
  data: IChangeVariationProduct
  variations: IFrontVariation[]
  sellerId: string
  productId?: string
  // подкатегория основного товара: показываем только товары с ней либо без категории
  parentSubCategoryId?: string
  openCreateProductModal: () => void
  openUpdateProductModal: (variation: IFrontVariation, variationIndex: number) => void
  onClose: () => void
}

export const ChooseVariationProductModal: React.FC<IChooseVariationProductModalProps> =
  React.memo(({ data, variations, sellerId, productId, parentSubCategoryId, openCreateProductModal, openUpdateProductModal, onClose }) => {
    const { visible, variation, variationIndex } = data
    const role = useAppSelector(appSelectors.selectRole)

    const [globalSearch, setGlobalSearch] = React.useState('')

    const [selectedProduct, setSelectedProduct] =
      React.useState<IFrontVariationProduct | null>(null)

    const [currentPage, setCurrentPage] = React.useState(1)
    const [total, setTotal] = React.useState(0)
    const pageSize = 7

    const [isLoading, setIsLoading] = React.useState(false)
    const [products, setProducts] = React.useState<IProductResponse[]>([])

    const [nameSearch, setNameSearch] = React.useState('')
    const nameSearchRef = React.useRef<InputRef>(null)

    const [status, setStatus] = React.useState<EProductStatus | undefined>(
      undefined
    )

    React.useEffect(() => {
      if (visible) {
        getProducts((currentPage - 1) * pageSize, status)
      }
    }, [visible])

    React.useEffect(() => {
      if (variation?.product) {
        setSelectedProduct(variation.product)
      }
    }, [variation])

    const closeModal = () => {
      setSelectedProduct(null)
      onClose()
    }

    const chooseProduct = () => {
      if (!selectedProduct) {
        return showMessage('info', 'Выберите продукт из таблицы', '')
      }
      if (variations.some(item => item?.product?.id === selectedProduct.id)) {
        return showMessage('warning', 'Данный товар уже выбран для другой вариации', '')
      }
      openUpdateProductModal({
        ...variation,
        product: selectedProduct
      }, variationIndex as number)
      setSelectedProduct(null)
    }

    const getProducts = async (
      skip: number,
      status: EProductStatus | undefined,
      data?: IProductQueryFilters
    ) => {
      try {
        setIsLoading(true)
        const response = await productApi.getProducts({
          skip: String(skip),
          limit: String(pageSize),
          isHaveParent: false,
          excludeProductId: productId,
          all_fields: data?.all_fields ?? globalSearch,
          status,
          name: data?.name ?? nameSearch,
          seller_id: sellerId,
          // вариацией может стать товар той же подкатегории, что и основной, либо без категории
          subCategoryOrEmpty_id: parentSubCategoryId
        })
        setTotal(response.data.count)
        setProducts(response.data.products)
      } catch (e) {
        showServerError(e)
      } finally {
        setIsLoading(false)
      }
    }

    const onChangeGlobalSearch = (searchValue: string) => {
      getProducts(0, status, { all_fields: searchValue })
      setCurrentPage(1)
    }

    const debouncedGetProductsByGlobalSearch = React.useCallback(
      debounce(onChangeGlobalSearch, DEBOUNCE_DELAY),
      [nameSearch, status]
    )

    const onRowSelection = (
      _: React.Key[],
      selectedRows: IProductResponse[]
    ) => {
      setSelectedProduct({
        id: selectedRows[0]._id,
        name: selectedRows[0].name,
        price: selectedRows[0].price
      })
    }

    const onPageChange = (current: number) => {
      getProducts((current - 1) * pageSize, status)
      setCurrentPage(current)
    }

    const onSaveSearchName = (nameSearch: string) => {
      getProducts(0, status, { name: nameSearch })
      setCurrentPage(1)
    }

    const onTagClick = React.useCallback(
      async (e: MenuInfo, itemId: string) => {
        const action = getProductStatusAction(e.key, itemId)
        if (!action) {
          return
        }
        try {
          setIsLoading(true)
          await productApi.setActions(action)
          setProducts((prev) =>
            prev.map((el) => {
              if (el._id === itemId) {
                return {
                  ...el,
                  status: getStatusAfterAction(
                    e.key,
                    el.status,
                    role as ERoles
                  )
                }
              }
              return el
            })
          )
        } catch (e) {
          showServerError(e)
        } finally {
          setIsLoading(false)
        }
      },
      [role]
    )

    const onChangeStatus = (newStatus: EProductStatus | undefined) => {
      getProducts(0, newStatus)
      setCurrentPage(1)
    }

    const columns: ColumnsType<IProductResponse> = React.useMemo(
      () => [
        {
          title: 'Артикул',
          dataIndex: 'vendor_code',
          width: 101
        },
        {
          title: 'Фото',
          dataIndex: 'photo',
          width: 70,
          render: (_, { photo }) => {
            return photo && !!photo.length ? (
              <div
                style={{
                  width: 62,
                  height: 56,
                  backgroundImage: `url(${IMAGES_URL}${photo[0]})`,
                  backgroundSize: 'contain',
                  backgroundPosition: 'center',
                  backgroundRepeat: 'no-repeat'
                }}
              />
            ) : (
              <img
                src={noProductImage}
                alt='Изображение товара'
                width={62}
                height={56}
              />
            )
          }
        },
        {
          title: 'Наименование товара',
          dataIndex: 'name',
          width: 310,
          filterDropdown: ({ close }) => {
            return (
              <div
                className='filterWrapper'
                onKeyDown={(e) => e.stopPropagation()}
              >
                <Input
                  ref={nameSearchRef}
                  value={nameSearch}
                  size='middle'
                  onChange={(e) => setNameSearch(e.target.value)}
                  onPressEnter={() => {
                    onSaveSearchName(nameSearch)
                    close()
                  }}
                  placeholder='Наименование товара'
                  className='filterInput'
                />
                <div className='filterFooterButtons'>
                  <Button
                    type='primary'
                    onClick={() => {
                      onSaveSearchName(nameSearch)
                      close()
                    }}
                    icon={<SearchOutlined />}
                    size='small'
                    className='filterButton'
                  >
                    Найти
                  </Button>
                  <Button
                    onClick={() => {
                      setNameSearch('')
                      onSaveSearchName('')
                      close()
                    }}
                    size='small'
                    className='filterButton'
                  >
                    Очистить
                  </Button>
                  <Button
                    type='link'
                    size='small'
                    onClick={() => {
                      close()
                    }}
                  >
                    Закрыть
                  </Button>
                </div>
              </div>
            )
          },
          onFilterDropdownOpenChange: (visible) => {
            if (visible) {
              setTimeout(() => nameSearchRef.current?.focus(), 0)
            }
          },
          filterIcon: () => (
            <SearchOutlined
              style={{
                color: nameSearch ? '#1677ff' : 'rgba(0, 0, 0, 0.29)'
              }}
            />
          )
        },
        {
          // список уже отфильтрован по подкатегории основного товара (или без категории)
          title: 'Категория',
          dataIndex: 'category_id',
          width: 310,
          render: (_, { category_id, subCategory_id }) => {
            if (!category_id || !subCategory_id) {
              return (
                <Typography.Text style={{ opacity: 0.5 }}>
                  Не задана
                </Typography.Text>
              )
            }
            return (
              <>
                <Typography.Text>{category_id.name + ' >'}</Typography.Text>
                <br />
                <Typography.Text>{subCategory_id.name}</Typography.Text>
              </>
            )
          }
        },
        {
          title: 'Цена',
          dataIndex: 'price',
          width: 101,
          render: (_, { price }) => {
            return (
              <Typography.Text style={{ opacity: price ? 1 : 0.5 }}>
                {price ? getNormalizedPrice(price) : 'Не задана'}
              </Typography.Text>
            )
          }
        },
        {
          title: 'Статус',
          dataIndex: 'status',
          width: 136,
          render: (_, { status, _id }) => {
            if (role === ERoles.USER && status === EProductStatus.MODERATION) {
              return (
                <Tag color={productTags[status].color}>
                  {productTags[status].label}
                </Tag>
              )
            }
            return (
              <StatusDropdown
                items={getProductStatusItems(status, role as ERoles)}
                label={productTags[status].label}
                color={productTags[status].color}
                onClick={(e) => onTagClick(e, _id)}
              />
            )
          },
          filterDropdown: ({ close }) => {
            return (
              <div
                className='filterWrapper'
                onKeyDown={(e) => e.stopPropagation()}
              >
                <Radio.Group
                  onChange={(e) => {
                    setStatus(e.target.value)
                    onChangeStatus(e.target.value)
                    close()
                  }}
                  value={status}
                  style={{ display: 'block', marginBottom: 15 }}
                >
                  <Space direction='vertical' size={15}>
                    {Object.values(productTags).map((item) => (
                      <Radio key={item.value as string} value={item.value}>
                        <Tag color={item.color}>{item.label}</Tag>
                      </Radio>
                    ))}
                  </Space>
                </Radio.Group>
                <Button
                  onClick={() => {
                    setStatus(undefined)
                    onChangeStatus(undefined)
                    close()
                  }}
                  size='small'
                  className='filterButton'
                >
                  Очистить
                </Button>
                <Button
                  type='link'
                  size='small'
                  onClick={() => {
                    close()
                  }}
                >
                  Закрыть
                </Button>
              </div>
            )
          },
          filterIcon: () => (
            <FilterFilled
              style={{
                color: status ? '#1677ff' : 'rgba(0, 0, 0, 0.25)'
              }}
            />
          )
        }
      ],
      [
        products,
        nameSearch,
        status,
        sellerId
      ]
    )

    return (
      <Modal
        centered
        open={visible}
        footer={null}
        closable={false}
        onCancel={closeModal}
        width={1100}
      >
        <SelectedVariation
          variation={variation as IFrontVariation}
          onClose={closeModal}
        />
        <div className={styles.chooseVariationProductHeader}>
          <Input
            value={globalSearch}
            onChange={(e) => {
              setGlobalSearch(e.target.value)
              debouncedGetProductsByGlobalSearch(e.target.value)
            }}
            placeholder='Поиск'
            size='large'
            suffix={<SearchOutlined />}
            className='searchInput'
          />
          <Button type='primary' size='large' icon={<PlusOutlined />} onClick={openCreateProductModal}>
            Создать
          </Button>
        </div>
        <Table
          scroll={{
            x: 900
          }}
          columns={columns}
          dataSource={products}
          loading={isLoading}
          rowKey={(el) => el._id}
          rowSelection={{
            type: 'radio',
            selectedRowKeys: selectedProduct?.id ? [selectedProduct.id] : [],
            onChange: onRowSelection
          }}
          // выбор товара кликом по любому месту строки, не только по радио-кнопке
          onRow={(record) => ({
            onClick: () =>
              setSelectedProduct({
                id: record._id,
                name: record.name,
                price: record.price
              }),
            style: { cursor: 'pointer' }
          })}
          rowClassName='tableRow'
          footer={undefined}
          pagination={{
            current: currentPage,
            onChange: onPageChange,
            pageSize,
            total,
            showSizeChanger: false
          }}
        />
        <div className={styles.chooseVariationProductFooter}>
          <Button type='default' size='middle' onClick={closeModal}>
            Отменить
          </Button>
          <Button type='primary' size='middle' disabled={!selectedProduct} onClick={chooseProduct}>
            Выбрать
          </Button>
        </div>
      </Modal>
    )
  })
